import React, { useRef, useContext } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { PageAbsolutePath } from '../../../../consts/Common';
import { ButtonContinue } from '../../../common/Button/ButtonContinue';
import Table, { HeaderRow, TableRow } from '../../../common/Widgets/Table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck';
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons/faChevronDown';
import List from '../../../common/Widgets/List';
import Image from '../../../common/Widgets/Image';
import Card from '../../../common/Widgets/Card';
import { SiteContext } from '../../../../context/SiteContext';
import {
  InsuranceJourneyContext,
  InsuranceJourneyState
} from '../../../common/CommonTypes';
import useCarInsuranceController from '../../../../hooks/insuranceJourney/useCarInsuranceController';
import { getStartQuotePath } from '../InsuranceMethods';
import { useErrorBoundary } from 'react-error-boundary';

const LotusLandingPage = (): JSX.Element => {
  const navigate = useNavigate();
  const { showBoundary } = useErrorBoundary();
  const { site } = useContext(SiteContext);
  const { brand } = site;

  const phoneNumber = brand.brandPhoneNumbers.quotesAndRenewalsNumber;

  const scrollRef = useRef<null | HTMLDivElement>(null);

  const scrollOnClick = () => {
    scrollRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };

  const { pageEvents } = useOutletContext<InsuranceJourneyContext>();

  const { commonEvents } = pageEvents;

  const { setQuote } = commonEvents;

  const { getNewQuote } = useCarInsuranceController();

  const getNewQuoteSuccess = (quote: InsuranceJourneyState) => {
    setQuote({
      ...quote,
      tiaProductCode: site.brand.tiaProductCode
    });

    navigate(
      getStartQuotePath(
        site.user.isDealer ?? false,
        site.brand.useAlternateEligibility
      )
    );
  };

  const onBackendFailure = (err: string) => {
    showBoundary(new Error(err));
  };

  const getStarted = () => {
    getNewQuote(
      site.brand.brandName,
      site.user?.insuranceDealerCode ?? '',
      site.user.token,
      getNewQuoteSuccess,
      onBackendFailure
    );
  };

  const lotusTableHeader: HeaderRow = {
    columns: ['Lotus Guaranteed', 'Lotus Car Insurance', 'Top UK insurers']
  };

  const tickComponent = <FontAwesomeIcon icon={faCheck} />;

  const crossComponent = <FontAwesomeIcon icon={faXmark} />;

  const chevronComponent = <FontAwesomeIcon icon={faChevronDown} size='2xl' />;

  const lotusTableRows: TableRow[] = [
    { columns: ['Lotus genuine parts', tickComponent, crossComponent] },
    {
      columns: ['Lotus approved bodyshops', tickComponent, crossComponent]
    },
    { columns: ['Lotus trained technicians', tickComponent, crossComponent] },
    {
      columns: [
        'Lotus warranty and safety rating preserved by only fitting Lotus genuine parts',
        tickComponent,
        crossComponent
      ]
    }
  ];

  const standardCoverItems: string[] = [
    'Comprehensive UK & EU cover',
    'Courtesy car (subject to availability)',
    '24-hour accident recovery',
    'Replacement lock cover',
    '5 year repair guarantee',
    'Personal accident cover',
    'Personal possessions cover',
    'A brand new Lotus if yours is written off or stolen within 12 months of its new registration date'
  ];

  const extraCoverItems: string[] = [
    '£100,000 legal expenses',
    'No-claim discount protection',
    'Upgrade your courtesy car and stay mobile if your car is written-off or stolen',
    'Lost or stolen key cover up to £1,000'
  ];

  const getStartedRetrieveQuote = () => {
    navigate(PageAbsolutePath.Insurance.RetrieveQuote);
  };

  return (
    <div className='insurance-landing-page primary-section'>
      <div className='hero-image'>
        <div className='image-text'>
          <h1 className='main-header'>Insure, Assured.</h1>
          <h1 className='main-header'>With Lotus Car insurance</h1>
          <div className='flex-row login-buttons'>
            <ButtonContinue
              name='btnGetStarted'
              text='Start quote'
              onClick={getStarted}
              additionalClassNames='btn-hero-start'
            />
            <ButtonContinue
              name='btnRetrieveQuote'
              text='Request a call back'
              onClick={getStartedRetrieveQuote}
              additionalClassNames='btn-hero-retrieve'
            />
          </div>
          <h2>
            Or call{' '}
            <a className='calltext-big' href={`tel:${phoneNumber}`}>
              {phoneNumber}
            </a>
          </h2>
          <span className='chevron' onClick={scrollOnClick}>
            {chevronComponent}
          </span>
        </div>
      </div>
      <div
        ref={scrollRef}
        className='flex-row justify-center secondary-section'>
        <div className='margin-panel flex-row'>
          <div className='two-columns offset-smaller mobile-hide flex-column'>
            <Image
              id='lotus-image-1'
              altText='lotus-image-1'
              source='./brands/lotus/landing-02.jpg'
            />
          </div>
          <div className='two-columns offset-larger'>
            <h3>The cover your Lotus deserves</h3>
            <p className='second-font'>
              Only Lotus knows your car inside out, which is why you can trust
              Lotus Car Insurance to provide the protection you need. By
              combining beautifully designed cars with a seamless insurance
              solution we have created a car ownership experience that is second
              to none.
            </p>
            <p className='second-font'>
              Please note that we are currently unable to cover the Eletre R.
            </p>
          </div>
          <div className='two-columns offset-smaller desktop-hide mobile-image-left'>
            <Image
              id='lotus-image-1'
              altText='lotus-image-1'
              source='./brands/lotus/landing-02.jpg'
            />
          </div>
        </div>
        <div className='margin-panel flex-row'>
          <div className='two-columns offset-smaller desktop-hide align-right'>
            <img
              id='lotus-image-2'
              alt='lotus-image-2'
              src='./brands/lotus/landing-03.jpg'
              className='mobile-image-right'
            />
          </div>
          <div className='two-columns offset-larger'>
            <h4>Think all insurers are the same?</h4>
            <h4>Think again.</h4>
            <p className='second-font'>
              Our exclusive repair benefits guarantee what many insurers don't.
            </p>
            <Table
              id='landing-page-table'
              header={lotusTableHeader}
              rows={lotusTableRows}
              addendum='Source: Research of UK motor insurer policy wordings, August 2024.'
            />
            <div>
              <ButtonContinue
                name='btnGetStarted'
                text='Start quote'
                onClick={getStarted}
                additionalClassNames='btn-start'
              />
            </div>
          </div>
          <div className='two-columns offset-smaller mobile-hide no-padding'>
            <Image
              id='lotus-image-2'
              altText='lotus-image-2'
              source='./brands/lotus/landing-03.jpg'
              height={600}
            />
          </div>
          <div className='flex-row m1-top'>
            <Image
              id='lotus-image-3'
              altText='lotus-image-3'
              source='./brands/lotus/landing-04.jpg'
            />
          </div>
        </div>
      </div>
      <div className='tertiary-section justify-center flex-row'>
        <div className='flex-row lists margin-panel'>
          <div className='two-columns standard-cover'>
            <List
              id='standard-cover'
              title='Standard Cover'
              listItems={standardCoverItems}
            />
            <ButtonContinue
              name='btnGetStarted'
              text='Start quote'
              onClick={getStarted}
              additionalClassNames='btn-start'
            />
          </div>
          <div className='two-columns optional-extra'>
            <List
              id='extra-cover'
              title='Optional Extra Cover'
              listItems={extraCoverItems}
            />
          </div>
        </div>
        <div className='flex-row margin-panel'>
          <div className='two-columns'>
            <Image
              id='lotus-image-4'
              altText='lotus-image-4'
              source='./brands/lotus/landing-05.jpg'
            />
          </div>
          <div className='two-columns complimentary-voucher'>
            <Card
              id='complimentary-voucher'
              title='Complimentary £250 excess return voucher'
              description='Receive a free one-year Excess Return Voucher to help protect your brand new Lotus. It works alongside your car insurance policy, paying up to £250 of your excess costs in the unfortunate event of an accident repair claim. To apply, simply request a 12-month Lotus Car Insurance quote.'
            />
            <ButtonContinue
              name='btnGetStarted'
              text='Start quote'
              onClick={getStarted}
              additionalClassNames='btn-start'
            />
          </div>
        </div>
      </div>
      <div className='logo-section'>
        <Image
          id='lotus-roundel-1'
          altText='lotus-roundel-4'
          source='./brands/lotus/LOTUS_Roundel_Primary_RGB.png'
        />
      </div>
      <div className='subtext'>
        The Excess Return Voucher offer is available to retail Lotus customers
        and is based on asking for a 12-month insurance quotation. Voucher
        cannot be redeemed against breakdown, misfuelling or standalone glass
        claims. Excess Return Voucher terms and conditions apply. This offer may
        be withdrawn or amended at any time.
      </div>
    </div>
  );
};

export default LotusLandingPage;
